import type { DraftView, EditLockView } from '@geoic/shared';
import { api, type ListResult } from '@/lib/api-client.js';

/**
 * Admin javobidagi hujjat.
 *
 * Maydonlar to'plami kolleksiyaga qarab har xil — sahifada `blocks`,
 * yangilikda `body`. Umumiy qismi shu yerda tiplangan, qolganiga
 * har bir bo'lim o'z tipini beradi.
 */
export type ContentRecord = {
  _id: string;
  slug?: string;
  isPublished: boolean;
  publishedAt: string | null;
  order?: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  draft: Record<string, unknown> | null;
  draftAt: string | null;
  draftBy: string | null;
  editLock: { userId: string; userName: string; expiresAt: string } | null;
} & Record<string, unknown>;

export type ContentListParams = {
  page?: number;
  limit?: number;
  search?: string;
  isPublished?: boolean;
  deleted?: boolean;
  categoryId?: string;
  sort?: 'order' | 'createdAt' | 'title' | 'publishedAt';
  direction?: 'asc' | 'desc';
};

export type PreviewLink = {
  token: string;
  expiresAt: string;
  url: string;
  path: string | null;
};

/**
 * Bitta kontent kolleksiyasi uchun API.
 *
 * Serverda 11 ta kolleksiya bitta `createContentController` dan yasalgan —
 * shuning uchun mijozda ham bitta fabrika yetarli. Har bir bo'lim uchun
 * alohida yozilsa, `?deleted=true` yoki qulf heartbeat'i birortasida
 * unutilardi.
 */
export function createContentApi<T extends ContentRecord = ContentRecord>(basePath: string) {
  return {
    basePath,

    list: (params: ContentListParams, signal?: AbortSignal) =>
      api.get<ListResult<T>>(basePath, { ...params }, signal),

    one: (id: string) => api.get<{ data: T }>(`${basePath}/${id}`).then((r) => r.data),

    create: (body: unknown) => api.post<{ data: T }>(basePath, body).then((r) => r.data),

    update: (id: string, body: unknown) =>
      api.patch<{ data: T }>(`${basePath}/${id}`, body).then((r) => r.data),

    setPublished: (id: string, isPublished: boolean) =>
      api.post<{ data: T }>(`${basePath}/${id}/publish`, { isPublished }).then((r) => r.data),

    remove: (id: string) => api.delete<void>(`${basePath}/${id}`),
    restore: (id: string) => api.post<{ data: T }>(`${basePath}/${id}/restore`).then((r) => r.data),
    purge: (id: string) => api.delete<void>(`${basePath}/${id}/purge`),

    reorder: (items: ReadonlyArray<{ _id: string; order: number }>) =>
      api.post<{ data: { updated: number } }>(`${basePath}/reorder`, { items }),

    // ─── Avtosaqlash va qulf ─────────────────────────────────────────────────

    saveDraft: (id: string, data: Record<string, unknown>) =>
      api.put<{ data: DraftView }>(`${basePath}/${id}/draft`, { data }).then((r) => r.data),

    discardDraft: (id: string) => api.delete<void>(`${basePath}/${id}/draft`),

    lock: (id: string, force = false) =>
      api.post<{ data: EditLockView }>(`${basePath}/${id}/lock`, { force }).then((r) => r.data),

    unlock: (id: string) => api.delete<void>(`${basePath}/${id}/lock`),

    previewLink: (id: string) =>
      api.post<{ data: PreviewLink }>(`${basePath}/${id}/preview-token`).then((r) => r.data),
  };
}

export type ContentApi<T extends ContentRecord = ContentRecord> = ReturnType<
  typeof createContentApi<T>
>;
